import { z } from 'zod';
import { uuidSchema } from './common.schema';

// ─── Enum constants ───────────────────────────────────────────────────────────

export const MilkProviderHealthRecordType = z.enum([
  'SEROLOGY',
  'OBSTETRIC_HISTORY',
  'HEALTH_HISTORY',
  'LIFESTYLE',
  'MEDICATION',
  'OTHER',
]);

export const MilkProviderHealthRecordResult = z.enum([
  'NEGATIVE',
  'POSITIVE',
  'INDETERMINATE',
  'PENDING',
  'N_A',
]);

export const MilkProviderAlcoholUse = z.enum([
  'NONE',
  'OCCASIONAL',
  'MODERATE',
  'REGULAR',
  'UNKNOWN',
]);

export const MilkProviderDietType = z.enum([
  'OMNIVORE',
  'VEGETARIAN',
  'VEGAN',
  'HALAL',
  'KOSHER',
  'RESTRICTED',
  'OTHER',
]);

// ─── Single health record response ───────────────────────────────────────────

export const MilkProviderHealthRecordSchema = z.object({
  health_record_id: uuidSchema,
  person_id:        uuidSchema,
  hospital_id:      uuidSchema.nullable(),

  // record classification
  record_type: MilkProviderHealthRecordType,

  // ── SEROLOGY ──────────────────────────────────────────────────────────────
  test_type:         z.string().nullable().optional(),
  test_date:         z.preprocess((v) => { if (v == null) return v; return v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v; }, z.iso.date().nullable().optional()),
  result:            MilkProviderHealthRecordResult.nullable().optional(),
  result_details:    z.string().nullable().optional(),
  lab_reference:     z.string().nullable().optional(),
  is_recurring_test: z.boolean(),
  next_test_date:    z.preprocess((v) => { if (v == null) return v; return v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === 'string' ? v.slice(0, 10) : v; }, z.iso.date().nullable().optional()),

  // ── OBSTETRIC_HISTORY ─────────────────────────────────────────────────────
  gravida: z.number().int().nonnegative().nullable().optional(),
  para:    z.number().int().nonnegative().nullable().optional(),

  // ── HEALTH_HISTORY ────────────────────────────────────────────────────────
  condition_flags:             z.record(z.string(), z.boolean()).nullable().optional(),
  past_medical_history:        z.string().nullable().optional(),
  current_medications_summary: z.string().nullable().optional(),
  allergies:                   z.string().nullable().optional(),
  history_of_mastitis:         z.boolean(),

  // ── LIFESTYLE ─────────────────────────────────────────────────────────────
  nicotine_use: z.boolean().nullable().optional(),
  alcohol_use:  MilkProviderAlcoholUse.nullable().optional(),
  diet_type:    MilkProviderDietType.nullable().optional(),
  notes:        z.string().nullable().optional(),
  warning:      z.string().nullable().optional(),

  // ── MEDICATION ────────────────────────────────────────────────────────────
  medications_detail: z.string().nullable().optional(),

  performed_by: uuidSchema.nullable().optional(),
  person_sr_no: z.number().int().nonnegative().nullable().optional(),
  created_at:   z.coerce.date(),
  is_deleted:   z.boolean(),
});
export type MilkProviderHealthRecord = z.infer<typeof MilkProviderHealthRecordSchema>;

// ─── GET query ────────────────────────────────────────────────────────────────

export const SerologyValidityStatus = z.enum(['VALID', 'DUE_SOON', 'OVERDUE']);
export type SerologyValidityStatus = z.infer<typeof SerologyValidityStatus>;

export const GetHealthRecordsQuerySchema = z.object({
  loginType: z.enum(['donor', 'mother']).optional().describe('Caller login context'),
});
export type GetHealthRecordsQuery = z.infer<typeof GetHealthRecordsQuerySchema>;

// ─── GET response ─────────────────────────────────────────────────────────────

export const GetHealthRecordsResponseSchema = z.object({
  items: z.array(MilkProviderHealthRecordSchema),
});
export type GetHealthRecordsResponse = z.infer<typeof GetHealthRecordsResponseSchema>;

// ─── POST body ────────────────────────────────────────────────────────────────

export const CreateHealthRecordSchema = z.object({
  record_type: MilkProviderHealthRecordType,

  // SEROLOGY
  test_type:         z.string().max(100).nullable().optional(),
  test_date:         z.string().nullable().optional(),          // "YYYY-MM-DD"
  result:            MilkProviderHealthRecordResult.nullable().optional(),
  result_details:    z.string().nullable().optional(),
  lab_reference:     z.string().max(100).nullable().optional(),
  is_recurring_test: z.boolean().default(false),
  next_test_date:    z.string().nullable().optional(),          // "YYYY-MM-DD"

  // OBSTETRIC_HISTORY
  gravida: z.number().int().nonnegative().nullable().optional(),
  para:    z.number().int().nonnegative().nullable().optional(),

  // HEALTH_HISTORY
  condition_flags:             z.record(z.string(), z.boolean()).nullable().optional(),
  past_medical_history:        z.string().nullable().optional(),
  current_medications_summary: z.string().nullable().optional(),
  allergies:                   z.string().nullable().optional(),
  history_of_mastitis:         z.boolean().default(false),

  // LIFESTYLE
  nicotine_use: z.boolean().nullable().optional(),
  alcohol_use:  MilkProviderAlcoholUse.nullable().optional(),
  diet_type:    MilkProviderDietType.nullable().optional(),
  notes:        z.string().nullable().optional(),
  warning:      z.string().nullable().optional(),

  // MEDICATION
  medications_detail: z.string().nullable().optional(),

  performed_by: uuidSchema.nullable().optional(),
});
export type CreateHealthRecord = z.infer<typeof CreateHealthRecordSchema>;

// ─── PATCH body (all fields optional) ────────────────────────────────────────

export const UpdateHealthRecordSchema = CreateHealthRecordSchema.partial();
export type UpdateHealthRecord = z.infer<typeof UpdateHealthRecordSchema>;

// ─── Bulk POST body ───────────────────────────────────────────────────────────

// Individual record inside a bulk request — only record_type + type-specific fields (no performed_by)
export const BulkRecordItemSchema = CreateHealthRecordSchema.omit({ performed_by: true });
export type BulkRecordItem = z.infer<typeof BulkRecordItemSchema>;

export const BulkCreateHealthRecordSchema = z.object({
  person_id:    uuidSchema,
  hospital_id:  uuidSchema,
  performed_by: uuidSchema.nullable().optional(),
  records:      z.array(BulkRecordItemSchema).min(1).max(20),
});
export type BulkCreateHealthRecord = z.infer<typeof BulkCreateHealthRecordSchema>;

// ─── Structured health-screening POST ────────────────────────────────────────
// Each section maps to one record_type.  SEROLOGY is an array (one row per test);
// all other sections are objects (upserted — one row per person per type).

export const SerologyRecordInputSchema = z.object({
  test_type:         z.string().max(100).describe('Test name, e.g. HIV, HBsAg, HCV, CMV'),
  test_date:         z.string().nullable().optional().describe('Sample date YYYY-MM-DD'),
  result:            MilkProviderHealthRecordResult.nullable().optional(),
  result_details:    z.string().nullable().optional().describe('Lab notes / titre values'),
  lab_reference:     z.string().max(100).nullable().optional().describe('Lab accession number'),
  is_recurring_test: z.boolean().default(false).describe('TRUE = must be repeated periodically'),
  next_test_date:    z.string().nullable().optional().describe('Due date for next repeat YYYY-MM-DD'),
  attachment_url:    z.string().url().nullable().optional().describe('URL of uploaded document (use the document upload API first)'),
});
export type SerologyRecordInput = z.infer<typeof SerologyRecordInputSchema>;

// Explicit condition_flags keys to match the documented JSONB contract
export const ConditionFlagsSchema = z.object({
  diabetes:                z.boolean().optional(),
  hypertension:            z.boolean().optional(),
  thyroid_disorder:        z.boolean().optional(),
  autoimmune_disease:      z.boolean().optional(),
  hepatitis:               z.boolean().optional(),
  hiv:                     z.boolean().optional(),
  tuberculosis:            z.boolean().optional(),
  mental_health_condition: z.boolean().optional(),
  other:                   z.boolean().optional(),
});
export type ConditionFlags = z.infer<typeof ConditionFlagsSchema>;

export const HealthHistoryInputSchema = z.object({
  condition_flags:             ConditionFlagsSchema.nullable().optional(),
  past_medical_history:        z.string().nullable().optional(),
  current_medications_summary: z.string().nullable().optional(),
  allergies:                   z.string().nullable().optional(),
  history_of_mastitis:         z.boolean().default(false),
});
export type HealthHistoryInput = z.infer<typeof HealthHistoryInputSchema>;

export const LifestyleInputSchema = z.object({
  nicotine_use: z.boolean().nullable().optional(),
  alcohol_use:  MilkProviderAlcoholUse.nullable().optional(),
  diet_type:    MilkProviderDietType.nullable().optional(),
  notes:        z.string().nullable().optional(),
  warning:      z.string().nullable().optional(),
});
export type LifestyleInput = z.infer<typeof LifestyleInputSchema>;

export const ObstetricHistoryInputSchema = z.object({
  gravida: z.number().int().nonnegative().nullable().optional(),
  para:    z.number().int().nonnegative().nullable().optional(),
});
export type ObstetricHistoryInput = z.infer<typeof ObstetricHistoryInputSchema>;

export const MedicationInputSchema = z.object({
  medications_detail: z.string().nullable().optional(),
});
export type MedicationInput = z.infer<typeof MedicationInputSchema>;

/**
 * Structured health-screening POST body.
 * At least one section is required (validated in the service layer).
 */
export const CreateHealthScreeningSchema = z.object({
  person_id:         uuidSchema,
  hospital_id:       uuidSchema,
  performed_by:      uuidSchema.nullable().optional(),

  serology:          z.array(SerologyRecordInputSchema).min(1).optional()
                       .describe('One row per infectious-disease test (always creates new records)'),
  health_history:    HealthHistoryInputSchema.optional()
                       .describe('Medical history checklist — upserted (one record per person)'),
  lifestyle:         LifestyleInputSchema.optional()
                       .describe('Lifestyle factors — upserted (one record per person)'),
  obstetric_history: ObstetricHistoryInputSchema.optional()
                       .describe('Obstetric history — upserted (one record per person)'),
  medication:        MedicationInputSchema.optional()
                       .describe('Medication declarations — upserted (one record per person)'),
});
export type CreateHealthScreening = z.infer<typeof CreateHealthScreeningSchema>;

// ─── Health-screening structured response ────────────────────────────────────

// Attachment stored in S3 (file_path is the S3 key / URL)
export const HealthRecordAttachmentSchema = z.object({
  attachment_id:   uuidSchema,
  attachment_type: z.string(),
  reason:          z.string().nullable(),
  file_name:       z.string(),
  file_path:       z.string(),   // S3 key / URL
  file_size_bytes: z.number().int().nullable(),
  mime_type:       z.string().nullable(),
  description:     z.string().nullable(),
  uploaded_by:     uuidSchema,
  uploaded_at:     z.coerce.date(),
});
export type HealthRecordAttachment = z.infer<typeof HealthRecordAttachmentSchema>;

export const SerologyRecordResponseSchema = z.object({
  health_record_id:  uuidSchema,
  record_type:       z.literal('SEROLOGY'),
  test_type:         z.string().nullable(),
  test_date:         z.coerce.date().nullable(),
  result:            MilkProviderHealthRecordResult.nullable(),
  result_details:    z.string().nullable(),
  lab_reference:     z.string().nullable(),
  is_recurring_test: z.boolean(),
  next_test_date:    z.coerce.date().nullable(),
  validity_status:   SerologyValidityStatus.nullable().describe('VALID | DUE_SOON (≤30 days) | OVERDUE — null for non-recurring tests'),
  created_at:        z.coerce.date(),
  is_deleted:        z.boolean(),
  attachments:       z.array(HealthRecordAttachmentSchema).optional(),
});

export const HealthHistoryResponseSchema = z.object({
  health_record_id:            uuidSchema,
  record_type:                 z.literal('HEALTH_HISTORY'),
  condition_flags:             z.record(z.string(), z.boolean()).nullable(),
  past_medical_history:        z.string().nullable(),
  current_medications_summary: z.string().nullable(),
  allergies:                   z.string().nullable(),
  history_of_mastitis:         z.boolean(),
  created_at:                  z.coerce.date(),
  is_deleted:                  z.boolean(),
});

export const LifestyleResponseSchema = z.object({
  health_record_id: uuidSchema,
  record_type:      z.literal('LIFESTYLE'),
  nicotine_use:     z.boolean().nullable(),
  alcohol_use:      MilkProviderAlcoholUse.nullable(),
  diet_type:        MilkProviderDietType.nullable(),
  notes:            z.string().nullable(),
  warning:          z.string().nullable(),
  created_at:       z.coerce.date(),
  is_deleted:       z.boolean(),
});

export const ObstetricHistoryResponseSchema = z.object({
  health_record_id: uuidSchema,
  record_type:      z.literal('OBSTETRIC_HISTORY'),
  gravida:          z.number().nullable(),
  para:             z.number().nullable(),
  created_at:       z.coerce.date(),
  is_deleted:       z.boolean(),
});

export const MedicationResponseSchema = z.object({
  health_record_id:   uuidSchema,
  record_type:        z.literal('MEDICATION'),
  medications_detail: z.string().nullable(),
  created_at:         z.coerce.date(),
  is_deleted:         z.boolean(),
});

export const HealthScreeningResponseSchema = z.object({
  person_id:         uuidSchema,
  hospital_id:       uuidSchema,
  performed_by:      uuidSchema.nullable(),
  serology:          z.array(SerologyRecordResponseSchema).optional(),
  health_history:    HealthHistoryResponseSchema.optional(),
  lifestyle:         LifestyleResponseSchema.optional(),
  obstetric_history: ObstetricHistoryResponseSchema.optional(),
  medication:        MedicationResponseSchema.optional(),
});
export type HealthScreeningResponse = z.infer<typeof HealthScreeningResponseSchema>;

// ─── Grouped health-screening response (GET by person) ───────────────────────
// The GET /:person_id endpoint groups records by admission cycle (person_sr_no).
// Each group represents one admission; the most recent is ACTIVE, earlier ones PAST.

export const HealthScreeningGroupStatus = z.enum(['ACTIVE', 'PAST']);
export type HealthScreeningGroupStatus = z.infer<typeof HealthScreeningGroupStatus>;

export const HealthScreeningGroupSchema = HealthScreeningResponseSchema.extend({
  person_sr_no: z.number().int().nonnegative().nullable().describe('Admission cycle number; 1 = first registration, increments on re-admit'),
  test_date:    z.string().nullable().describe('Earliest created_at in this admission cycle (ISO 8601)'),
  status:       HealthScreeningGroupStatus.describe('ACTIVE for the latest admission cycle, PAST for earlier ones'),
});
export type HealthScreeningGroup = z.infer<typeof HealthScreeningGroupSchema>;

// ─── Screening approval / rejection ──────────────────────────────────────────

/**
 * POST body for manually approving a donor's screening.
 * The nurse confirms that consent + all criteria are met.
 */
export const ApproveScreeningRequestSchema = z.object({
  approved_by: uuidSchema.describe('UUID of the nurse / staff member approving the screening'),
});
export type ApproveScreeningRequest = z.infer<typeof ApproveScreeningRequestSchema>;

/**
 * POST body for rejecting a donor's screening.
 * The nurse can optionally specify a reason.
 */
export const RejectScreeningRequestSchema = z.object({
  rejected_by: uuidSchema.describe('UUID of the nurse / staff member rejecting the screening'),
  rejection_reason: z.string().max(1000).optional().nullable().describe('Optional reason for rejection'),
});
export type RejectScreeningRequest = z.infer<typeof RejectScreeningRequestSchema>;

// ─── Bulk screening re-evaluation ────────────────────────────────────────────

/**
 * POST body for re-evaluating every donor of a hospital.
 * Used after the hospital's blood-test validity settings change, since that
 * alters the verdict for donors whose serology is unchanged.
 */
export const ReEvaluateScreeningRequestSchema = z.object({
  hospital_id: uuidSchema.describe('Hospital whose donors should be re-evaluated'),
});
export type ReEvaluateScreeningRequest = z.infer<typeof ReEvaluateScreeningRequestSchema>;

export const ReEvaluateScreeningResponseSchema = z.object({
  evaluated: z.number().int().describe('Number of donors examined'),
  changed:   z.number().int().describe('Number of donors whose stored screening status or expiry changed'),
});
export type ReEvaluateScreeningResponse = z.infer<typeof ReEvaluateScreeningResponseSchema>;
